import Service from '@ember/service';
import ENV from 'tio-employee/config/environment';

export interface ForethoughtSeedConfig {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  zendeskJwt: string;
  tioUserJwt?: string;
  tioApiKey: string;
  email?: string;
  partnerName?: string;
  onReady?: () => void;
}

/**
 * Owns the Forethought widget embed. The widget snapshots its config when
 * embed.js initializes, so a token rotation means re-embedding the script;
 * the persistence exemption below keeps the conversation across that
 * re-embed (staging-verified 2026-07-27). Re-embeds are deferred while the
 * panel is open so it doesn't visibly snap shut.
 */
export default class ForethoughtService extends Service {
  #config: ForethoughtSeedConfig | null = null;
  #panelOpen = false;
  #reembedPending = false;

  // Overridable in tests so the suite never loads the real vendor script.
  embedSrc = 'https://solve-widget.forethought.ai/embed.js';

  // The widget lives outside Ember's DOM; singleton listener, detached in willDestroy.
  #onWidgetMessage = (e: MessageEvent) => {
    if (e.data?.event === 'forethoughtWidgetClosed') {
      this.#panelOpen = false;
      if (this.#reembedPending) {
        this.#reembedPending = false;
        this.#embed();
      }
    }
  };

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    window.addEventListener('message', this.#onWidgetMessage);
  }

  willDestroy() {
    super.willDestroy();
    window.removeEventListener('message', this.#onWidgetMessage);
  }

  get isSeeded() {
    return this.#config !== null;
  }

  seed(config: ForethoughtSeedConfig) {
    this.#config = config;
    this.#panelOpen = false;
    this.#reembedPending = false;
    this.#embed();
  }

  /**
   * Identity boundary (logout): tear down the widget and forget the config so
   * the next login in this tab can't reach the previous user's conversation.
   */
  reset() {
    this.#config = null;
    this.#panelOpen = false;
    this.#reembedPending = false;
    this.#removeWidgetDom();
  }

  /** The widget has no "opened" postMessage; the opener (zendesk service) reports it. */
  notifyPanelOpened() {
    this.#panelOpen = true;
  }

  /**
   * Re-embed with the rotated session token so Forethought stops forwarding a
   * stale one to the MCP endpoint (401s after ~1h otherwise). No-op before
   * seed() and for unchanged tokens; deferred while the panel is open.
   */
  updateUserJwt(tioUserJwt: string | undefined) {
    if (!this.#config || !tioUserJwt || tioUserJwt === this.#config.tioUserJwt) {
      return;
    }
    this.#config = { ...this.#config, tioUserJwt };
    if (this.#panelOpen) {
      this.#reembedPending = true;
      return;
    }
    this.#embed();
  }

  #removeWidgetDom() {
    document.querySelector('#forethought-chat')?.remove();
    document.querySelector('#forethought-widget-embed-script')?.remove();
  }

  #embed() {
    if (!this.#config) {
      return;
    }
    const config = this.#config;

    this.#removeWidgetDom();
    const script = document.createElement('script');
    script.setAttribute('src', this.embedSrc);
    script.setAttribute('id', 'forethought-widget-embed-script');
    script.setAttribute('data-dd-privacy', 'hidden');
    script.setAttribute('data-api-key', '21bf8592-1b59-4758-84ef-f82c33c6462e');

    script.setAttribute('data-ft-First-Name', config.firstName ?? '');
    script.setAttribute('data-ft-Last-Name', config.lastName ?? '');
    script.setAttribute('data-ft-Full-Name', config.fullName ?? '');
    script.setAttribute('data-ft-zendesk-jwt', config.zendeskJwt);
    if (config.tioUserJwt) {
      script.setAttribute('data-ft-tio-user-jwt', config.tioUserJwt);
    }
    script.setAttribute('data-ft-tio-api-key', config.tioApiKey);
    script.setAttribute('data-ft-Email', config.email ?? '');
    script.setAttribute('data-ft-Partner-Name', config.partnerName ?? '');
    script.setAttribute('data-ft-workflow-tag', ENV.tioEnvironment);
    script.setAttribute('config-ft-persistence-id', ENV.tioEnvironment);
    // Keeps the conversation across a JWT re-embed AND adopts the new value;
    // the ignore-only sibling would keep forwarding the stale token.
    script.setAttribute(
      'config-ft-ignore-and-update-persistence-parameters',
      'data-ft-tio-user-jwt'
    );
    script.setAttribute('position-x', 'left');

    const onReady = config.onReady;
    if (onReady) {
      script.addEventListener('load', () => {
        // Poll for Forethought global to be available (widget initializes async after script load)
        const maxWaitMs = 5000;
        const pollIntervalMs = 100;
        const startTime = Date.now();

        const checkForethought = () => {
          if (window.Forethought) {
            onReady();
            return;
          }
          if (Date.now() - startTime >= maxWaitMs) {
            onReady();
            return;
          }
          setTimeout(checkForethought, pollIntervalMs);
        };

        checkForethought();
      });
    }

    document.head.append(script);
  }
}
